import { useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { gradientCss, evenPositions, type GradientType } from "./gradientUtils";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { NoticeBox } from "@/components/NoticeBox";
import { copyText } from "@/lib/copyText";

const TOOL_INFO_CONTENT = {
  what: `Creates a CSS gradient between two or more colors, with a live preview, and generates the corresponding CSS code to copy directly into a project.`,
  how: `Choose your colors and gradient direction, add more color stops if you want a richer gradient, adjust until the preview looks right, and copy the generated CSS.`,
  tip: `Gradients with colors that are too close in hue can look muddy rather than smooth — a gradient between two genuinely different hues (rather than two shades of the same color) often reads as more intentional and polished. With three or more colors, keep an eye on the middle stops especially — an unplanned color sitting between two others can create a muddy transition zone that neither endpoint color would produce alone.`,
};

const MAX_COLORS = 6;

let nextId = 3;

export default function GradientGenerator() {
  const [colors, setColors] = useState<{ id: number; value: string; pos: number }[]>([
    { id: 1, value: "#6366f1", pos: 0 },
    { id: 2, value: "#ec4899", pos: 100 },
  ]);
  const [type, setType] = useState<GradientType>("linear");
  const [angle, setAngle] = useState([135]);

  const css = gradientCss(type, angle[0], colors);
  const fallback = [...colors].sort((a, b) => a.pos - b.pos)[0].value;
  const respace = (list: typeof colors) => { const p = evenPositions(list.length); return list.map((c, i) => ({ ...c, pos: p[i] })); };
  const [posNote, setPosNote] = useState("");
  const updatePos = (id: number, raw: number) => {
    const pos = Number.isFinite(raw) ? Math.min(100, Math.max(0, raw)) : 0;
    setPosNote(pos !== raw ? "Stop positions run from 0 to 100%, so your value was set to " + pos + "%." : "");
    setColors(prev => prev.map(c => c.id === id ? { ...c, pos } : c));
  };

  const updateColor = (id: number, value: string) =>
    setColors(prev => prev.map(c => c.id === id ? { ...c, value } : c));

  const addColor = () => {
    if (colors.length >= MAX_COLORS) return;
    setColors(prev => respace([...prev, { id: nextId++, value: "#22d3ee", pos: 100 }]));
  };

  const removeColor = (id: number) => {
    if (colors.length <= 2) return; // a gradient needs at least 2 colors
    setColors(prev => respace(prev.filter(c => c.id !== id)));
  };

  const { blocked } = useLogToolUsage(true, 3000, { ownActions: true });


  return (
    <ToolContainer>
      <div className="h-48 rounded-xl border mb-6" style={{ background: css }} />

      <div className="space-y-3 mb-4">
        <div className="flex flex-wrap gap-3">
          {colors.map((c, i) => (
            <div key={c.id} className="flex items-end gap-1.5">
              <div>
                <Label className="text-xs">Color {i + 1}</Label>
                <Input type="color" aria-label={`Color ${i + 1}`} value={c.value} onChange={e => updateColor(c.id, e.target.value)} className="w-16 h-9 mt-1" />
                <label className="text-xs text-muted-foreground flex items-center gap-1 mt-1">at
                  <input type="number" min={0} max={100} aria-label={`Color ${i + 1} position (%)`} value={c.pos} onChange={e => updatePos(c.id, Number(e.target.value))} className="w-14 border rounded px-1 bg-background" />%
                </label>
              </div>
              {colors.length > 2 && (
                <Button variant="ghost" size="icon" className="h-9 w-9 mb-0.5" aria-label={`Remove color ${i + 1}`} onClick={() => removeColor(c.id)}>
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          ))}
          {colors.length < MAX_COLORS && (
            <Button variant="outline" onClick={addColor} className="gap-1.5 self-end mb-0.5">
              <Plus className="h-3.5 w-3.5" /> Add Color
            </Button>
          )}
        </div>
        {posNote && <NoticeBox className="text-xs" role="status">{posNote}</NoticeBox>}
        <div className="flex gap-2" role="group" aria-label="Gradient type">
          {(["linear", "radial", "conic"] as const).map(t => (
            <Button key={t} size="sm" variant={type === t ? "default" : "outline"} aria-pressed={type === t} onClick={() => setType(t)}>{t[0].toUpperCase() + t.slice(1)}</Button>
          ))}
        </div>
        {type !== "radial" && <div className="max-w-sm">
          <Label>Angle: {angle[0]}°</Label>
          <Slider value={angle} onValueChange={setAngle} min={0} max={360} />
        </div>}
      </div>

      <div className="p-3 rounded-lg border bg-card font-mono text-sm break-all">{blocked ? "— blocked at today's usage limit —" : css}</div>
      <Button className="mt-3" disabled={blocked} onClick={() => { void copyText(`background: ${fallback};\nbackground: ${css};`, "CSS copied!"); }}>Copy CSS</Button>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
